import React, {useCallback} from 'react';
import {Stack} from '@mui/material';
import {
  designSystemToken,
  Label,
  Tooltip,
} from '@lightricks/react-design-system';
import getEngagementRateString from '@/utils/getEngagementRateString';
import getFollowersCountString from '@/utils/getFollowersCountString';
import translate from '@/utils/translate';
import LEGACY_COLORS from '@/config/legacyColors';
import {SocialNetworkInfo} from '@/types/socials';
import Conditional from '@/components/conditional';
import styles from '@/components/creator-card/CreatorSocialsIcons.module.scss';
import SocialStat from '@/components/creator-card/SocialStat';
import Link from '@/components/link';
import SocialNetworkIcon from '@/components/social-network-icon';
import useFilteredTiktokSocialNetworks from '@/hooks/use-filtered-tiktok-social-networks';
import useSortedSocialNetworks from '@/hooks/use-sorted-social-networks';

type CreatorSocialsIconsProps = {
  socialNetworks: SocialNetworkInfo[];
  selectedPlatformProvider?: string;
  onClickSocialNetwork?: (networkProvider: string) => void;
  /** Use with creatorId to pass a stable (provider, creatorId) handler and avoid per-row callback creation */
  onClickSocialNetworkWithId?: (
    networkProvider: string,
    creatorId: string
  ) => void;
  creatorId?: string;
};

function CreatorSocialsIcons({
  socialNetworks,
  selectedPlatformProvider,
  onClickSocialNetwork,
  onClickSocialNetworkWithId,
  creatorId,
}: CreatorSocialsIconsProps) {
  const SOCIAL_NETWORK_ICON_SIZE = 29;
  const sortedSocialNetworks = useSortedSocialNetworks(
    socialNetworks,
    selectedPlatformProvider
  ) as SocialNetworkInfo[];
  const filteredSocialNetworks =
    useFilteredTiktokSocialNetworks(sortedSocialNetworks);

  const getSocialNetworkIconColor = (networkProvider: string) => {
    if (selectedPlatformProvider !== 'all') {
      return networkProvider.includes(selectedPlatformProvider || '')
        ? LEGACY_COLORS.ALMOST_BLACK
        : designSystemToken('semantic.fg.secondary');
    }

    return LEGACY_COLORS.ALMOST_BLACK;
  };

  const handleSocialClick = useCallback(
    (e: React.MouseEvent, provider: string) => {
      e.stopPropagation();
      if (onClickSocialNetworkWithId && creatorId != null) {
        onClickSocialNetworkWithId(provider, creatorId);
      } else {
        onClickSocialNetwork?.(provider);
      }
    },
    [creatorId, onClickSocialNetworkWithId, onClickSocialNetwork]
  );

  return (
    <div className={styles.socialNetworksContainer}>
      {filteredSocialNetworks.map((network: SocialNetworkInfo) => {
        const handle = network.handle?.trim();
        return (
          <Tooltip
            key={network.provider}
            type="light"
            title={
              <Stack direction="column">
                <Conditional condition={!!handle}>
                  <Label
                    size="md"
                    color={designSystemToken('semantic.fg.primary')}
                  >
                    {handle}
                  </Label>
                </Conditional>
                <SocialStat
                  title={
                    network.provider === 'youtube'
                      ? translate('Subscribers')
                      : translate('followers')
                  }
                  value={network.followersCount}
                  titleColor={designSystemToken('semantic.fg.secondary')}
                  valueColor={designSystemToken('semantic.fg.primary')}
                  formattingFunc={getFollowersCountString}
                />
                <Conditional condition={network.provider !== 'youtube'}>
                  <SocialStat
                    title={translate('Eng rate')}
                    value={network.engagementRate}
                    titleColor={designSystemToken('semantic.fg.secondary')}
                    valueColor={designSystemToken('semantic.fg.primary')}
                    formattingFunc={getEngagementRateString}
                  />
                </Conditional>
              </Stack>
            }
            placement="top-start"
            arrow
          >
            <div key={network.provider}>
              <Link
                to={network.url}
                target="_blank"
                rel="noopener noreferrer"
                keepBrandIdIfPresent={false}
                onClick={(e) => handleSocialClick(e, network.provider)}
              >
                <div
                  className={`${styles.socialIconContainer} ${
                    network.provider.includes(selectedPlatformProvider || '')
                      ? styles.selected
                      : ''
                  }`}
                >
                  <SocialNetworkIcon
                    name={network.provider}
                    width={SOCIAL_NETWORK_ICON_SIZE}
                    height={SOCIAL_NETWORK_ICON_SIZE}
                    color={getSocialNetworkIconColor(network.provider)}
                  />
                </div>
              </Link>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

export default CreatorSocialsIcons;
